import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Building2,
  Calculator,
  CheckSquare,
  LayoutDashboard,
  LogOut,
  Menu,
  Plug,
  Settings as SettingsIcon,
  Tag,
  User as UserIcon,
  X,
} from "lucide-react";
import { useLogout, useMe } from "@/hooks/useMe";
import { currentBrand } from "@/lib/brand";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
}

/**
 * Sidebar IA — keep this in sync with the spec in the dashboard README.
 * `end` ensures NavLink's `isActive` only matches the exact path on the
 * Overview row (otherwise every nested route would also highlight it).
 */
const navItems: NavItem[] = [
  { to: "/dashboard/app", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/app/profile", label: "Profile", icon: UserIcon },
  { to: "/dashboard/app/company", label: "My company", icon: Building2 },
  { to: "/dashboard/app/integrations", label: "Integrations", icon: Plug },
  { to: "/dashboard/app/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/dashboard/app/calculators", label: "Calculators", icon: Calculator },
  { to: "/dashboard/app/partners", label: "Partners", icon: Tag },
  { to: "/dashboard/app/settings", label: "Settings", icon: SettingsIcon },
];

export default function AppLayout() {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const brand = currentBrand();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/dashboard");
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Mobile top bar — only visible below md */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b bg-background px-4 md:hidden">
        <Link to="/dashboard/app" className="font-semibold">
          {brand.name}
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="rounded-md p-2 hover:bg-secondary"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </header>

      {/* Sidebar — fixed on desktop, slide-over on mobile */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 shrink-0 border-r bg-background transition-transform md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        )}
      >
        <div className="flex h-14 items-center justify-between border-b px-5">
          <Link to="/dashboard/app" className="font-semibold">
            {brand.name}
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="rounded-md p-2 hover:bg-secondary md:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex flex-col gap-0.5 p-3 text-sm">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2.5 rounded-md px-3 py-2",
                    isActive
                      ? "bg-secondary text-secondary-foreground"
                      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
                  )
                }
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="absolute inset-x-0 bottom-0 border-t p-3">
          <div className="rounded-md p-2 text-sm">
            <div className="font-medium">
              {me.data?.first_name || me.data?.email || "—"}
            </div>
            {me.data?.email ? (
              <div className="text-xs text-muted-foreground">{me.data.email}</div>
            ) : null}
            <button
              type="button"
              onClick={handleLogout}
              className="mt-2 flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <LogOut className="h-3.5 w-3.5" />
              Log out
            </button>
          </div>
        </div>
      </aside>

      {/* Backdrop when mobile sidebar is open */}
      {mobileOpen ? (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
        />
      ) : null}

      {/* Main content area, offset by sidebar width on md+ */}
      <main className="flex-1 px-4 pb-12 pt-20 md:ml-64 md:px-8 md:pt-8">
        <div className="mx-auto max-w-5xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
